// Component that shows/hides content based on user permissions.
//
// NOTE: this renders on many UI elements and re-renders with its parents, so it
// must stay cheap — no per-render logging or unmemoized work. (Previously it
// emitted 4-5 console.log calls per render, which flooded the console and made
// permission-gated pages very slow.)
import React, { useMemo } from 'react';
import { useUserPermissions } from '../../hooks/useUserPermissions';

const PermissionGate = ({
  children,
  permission,
  level,
  group,
  fallback = null,
  showLoading = true,
}) => {
  const { permissions, loading } = useUserPermissions();

  // Evaluate access once per (permissions, props) change instead of on every render.
  const allowed = useMemo(() => {
    if (!permissions) return false;

    // Specific permission
    if (permission && !permissions.permissions.includes(permission)) return false;

    // Minimum level
    if (level && permissions.level < level) return false;

    // Group(s): "Admin|developers" means Admin OR developers (case-insensitive)
    if (group) {
      const groupsToCheck = group.split('|').map((g) => g.trim().toLowerCase());
      const userGroups = Array.isArray(permissions.groups) ? permissions.groups : [];
      const hasGroup = groupsToCheck.some((required) =>
        userGroups.some((ug) => String(ug).trim().toLowerCase() === required)
      );
      if (!hasGroup) return false;
    }

    return true;
  }, [permissions, permission, level, group]);

  if (loading && showLoading) {
    return <div>Loading permissions...</div>;
  }
  if (!permissions) {
    return fallback;
  }

  return allowed ? children : fallback;
};

// Memoized so it doesn't re-run/re-render when a parent re-renders with the
// same props.
export default React.memo(PermissionGate);
